import { BulkAssignComponent } from './../../shared/models/bulk-assign-component';
import { PhotoGalleryComponent } from './../../components/photo-gallery/photo-gallery.component';
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as actions from '../../photo-store';
import { getSelectedProperty, getSelectedFiscalYear } from '../../../property/property-store/property.selectors';
import { AppState } from '../../../../app.reducers';
import { Property } from '../../../property/shared/models';
import { SubSink } from 'subsink';
import { combineLatest } from 'rxjs';
import { FiscalYear, ModalComponent, PhotoViewerComponent, ReportSettings, ReportConfigSettingConstants as ConfigConstants, ReportConfigSetting, } from 'app/internal/shared';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { PropertyPhotosRequest, PropertyPhoto, PhotoHelper } from '../../shared/models';
import { getPhotos, getBulkActionStatus } from '../../photo-store/photo.selectors';
import { UpdatePropertyPhotoAction, DeletePropertyPhotoAction } from '../../photo-store';
import { BulkPhotoReportSettings } from '../../shared/models/bulk-photo-report-settings';
import { ClearReportSettingsFromStateAction, FetchReportSettingsAction, SaveReportSettingsAction } from 'app/internal/reports/redux/reports.actions';
import { getReportSettings } from 'app/internal/reports/redux/reports.selectors';

@Component({
  templateUrl: './photo-list.component.html',
  styleUrls: ['./photo-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhotoListComponent implements OnInit, OnDestroy {
  property$: Observable<Property>;
  selectedFiscalYear$: Observable<FiscalYear>;
  propertyPhotos$: Observable<PropertyPhoto[]>;
  getBulkActionStatus$: Observable<string>;
  reportSettings$: Observable<ReportSettings>;

  selectedPropertyId: string;
  selectedFiscalYear: FiscalYear;
  photoToView: PropertyPhoto;
  reportSettings: ReportSettings;
  errorMsg = '';
  showFilter = false;
  isAllSelected = false;
  isBulkAssignPending = false;
  isBulkDeletePending = false;
  isBulkDownloadPending = false;
  isBulkReportSettingsUpdatePending = false;

  private subs = new SubSink();

  @ViewChild('photoGallery', { static: true }) public photoGallery: PhotoGalleryComponent;
  @ViewChild('photoViewer', { static: true }) public photoViewer: PhotoViewerComponent;
  @ViewChild('errorModal', { static: true }) public errorModal: ModalComponent;

  constructor(private store: Store<AppState>, private ref: ChangeDetectorRef) {
    this.property$ = this.store.pipe(select(getSelectedProperty));
    this.selectedFiscalYear$ = this.store.pipe(select(getSelectedFiscalYear));
    this.propertyPhotos$ = this.store.pipe(select(getPhotos));
    this.getBulkActionStatus$ = this.store.pipe(select(getBulkActionStatus));
    this.reportSettings$ = this.store.pipe(select(getReportSettings));
  }

  ngOnInit() {

    this.subs.sink = combineLatest([this.selectedFiscalYear$, this.property$])
      .pipe(filter(([fy, p]) => fy != null && p != null && !fy.requireSetup ))
      .subscribe(([fiscalYear, property]) => {
        if (this.selectedPropertyId != null && this.selectedPropertyId !== property.propertyId) {
          // property changed, no need to load this information because we'll be redirected to the dashboard.
          return;
        }

        this.selectedPropertyId = property.propertyId;
        this.selectedFiscalYear = fiscalYear;

        this.store.dispatch(new actions.LoadPropertyPhotosAction(new PropertyPhotosRequest(this.selectedPropertyId)));

        this.store.dispatch(new FetchReportSettingsAction({
          propertyId: this.selectedPropertyId,
          reportTypeId: 19,
          fiscalYear: 0, // not associated with a specific year, so set as 0.
        }));
      });

    this.subs.sink = this.reportSettings$
      .pipe(distinctUntilChanged())
      .subscribe(settings => {
        this.reportSettings = settings;
      });

    this.subs.sink = this.getBulkActionStatus$
      .subscribe((status) => {
        // bulk action has completed, reload photos
        if (status === 'complete') {
          if (!this.isBulkDownloadPending) {
            this.store.dispatch(new actions.LoadPropertyPhotosAction(new PropertyPhotosRequest(this.selectedPropertyId)));
          }
          this.clearBulkActionFlags();
        } else if (status === 'failed') {
          if (this.isBulkAssignPending) {
            this.errorMsg = 'Uh oh. An error occured while assigning the selected photos to a component.';
          } else if (this.isBulkDeletePending) {
            this.errorMsg = 'Uh oh. An error occured while deleting the selected photos. Please try again.';
          } else if (this.isBulkDownloadPending) {
            this.errorMsg = 'Uh oh. An error occured while downloading the selected photos. ' +
                        'If a downloading a large number of photos, try selecting a smaller set and try again.';
          } else {
            this.errorMsg = 'Uh oh. An error occured. Please try again.';
          }

          this.ref.detectChanges();
          this.errorModal.show();
          this.clearBulkActionFlags();
        }
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store.dispatch(new actions.ClearPropertyPhotos());
    this.store.dispatch(new ClearReportSettingsFromStateAction());
  }

  getViewHeight(fiscalYear: FiscalYear) {
    let headerHeight = 102;
    if (fiscalYear != null && fiscalYear.isFutureFiscalYear) {
      headerHeight = 161;
    }
    return 'calc(100vh - ' + headerHeight + 'px)';
  }

  private clearBulkActionFlags() {
    this.isBulkAssignPending = false;
    this.isBulkDeletePending = false;
    this.isBulkDownloadPending = false;
    this.isBulkReportSettingsUpdatePending = false;
  }

  
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onToggleFilter() {

  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  showComponentAssignmentPane() {

  }

  openPhotoDetailPanel(photo: PropertyPhoto) {
    this.photoToView = photo;
  }

  closeDetailsPane() {
    this.photoToView = null;
    this.photoGallery.clearPhotoToView();
  }

  onPhotoUpdated(photo: PropertyPhoto) {
    this.store.dispatch(new UpdatePropertyPhotoAction(photo));
  }

  onDeletePhoto() {
    this.store.dispatch(new DeletePropertyPhotoAction(this.photoToView.propertyPhotoId));
    this.closeDetailsPane();
  }

  onDownloadPhoto() {
    this.store.dispatch(new actions.DownloadPropertyPhotoAction(this.photoToView.propertyPhotoId));
  }

  onUploadPhotosComplete(hasUploadedPhotos: boolean) {
    if (hasUploadedPhotos) {
      this.store.dispatch(new actions.LoadPropertyPhotosAction(new PropertyPhotosRequest(this.selectedPropertyId)));
    }
  }

  onBulkDownload(photoIds: string[]) {
    this.isBulkDownloadPending = true;
    this.store.dispatch(new actions.BulkDownloadPropertyPhotosAction(photoIds));
  }

  onBulkAssignComponent(bulkAssignModel: BulkAssignComponent) {
    this.isBulkAssignPending = true;
    this.store.dispatch(new actions.BulkAssignPropertyPhotosToComponentAction(bulkAssignModel));
  }

  onBulkUpdateReportSettings(bulkReportSettingsModel: BulkPhotoReportSettings) {
    this.isBulkReportSettingsUpdatePending = true;
    this.store.dispatch(new actions.BulkUpdatePropertyPhotosReportSettingsAction(bulkReportSettingsModel));
  }

  onBulkDelete(photoIds: string[]) {
    this.isBulkDeletePending = true;
    this.store.dispatch(new actions.BulkDeletePropertyPhotosAction(photoIds));
  }

  openPhotoViewer() {
    this.photoViewer.showModal();
  }

  onViewerEditPhoto(photo: PropertyPhoto) {
    this.photoToView = photo;
    this.photoGallery.setPhotoToView(photo);
  }

  onViewerDownloadPhoto(photoId: string) {
    this.store.dispatch(new actions.DownloadPropertyPhotoAction(photoId));
  }

  onSortChanged(sortSettings: any) {
    const configSettings: ReportConfigSetting = this.reportSettings.configSettings ?? {};
    configSettings[ConfigConstants.SortBy] = sortSettings.sortBy;
    configSettings[ConfigConstants.SortDesc] = sortSettings.sortDesc.toString();
    this.reportSettings.configSettings = configSettings;
    this.store.dispatch(new SaveReportSettingsAction(this.reportSettings));
  }

  sortPhotos(photos: PropertyPhoto[]) : PropertyPhoto[] {
    return PhotoHelper.sortBySettings(photos, this.reportSettings);
  }
}
